import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface OrganizationSetting {
  id: string;
  org_id: string;
  key: string;
  value: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

/**
 * React Query-based organization settings management
 * Provides single source of truth per organization ID
 */
export const useOrganizationSettingsQuery = (organizationId: string | null | undefined) => {
  const queryClient = useQueryClient();

  // Query for fetching settings
  const { data: settings = [], isLoading, error, refetch } = useQuery({
    queryKey: ['organization-settings', organizationId],
    queryFn: async (): Promise<OrganizationSetting[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('organization_settings')
        .select('*')
        .eq('org_id', organizationId)
        .order('key');

      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  // Mutation for updating/creating settings
  const updateMutation = useMutation({
    mutationFn: async ({
      key,
      value,
      description,
    }: {
      key: string;
      value: string;
      description?: string;
    }) => {
      if (!organizationId) throw new Error('No organization ID provided');

      const { data, error } = await supabase
        .from('organization_settings')
        .upsert(
          {
            org_id: organizationId,
            key,
            value,
            description,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'org_id,key',
          }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch the settings query
      queryClient.invalidateQueries({ 
        queryKey: ['organization-settings', organizationId] 
      });

      console.log('[SETTINGS-QUERY] Updated setting:', data.key, '→', data.value);

      toast({
        title: 'Success',
        description: 'Setting updated successfully',
      });
    },
    onError: (error: any) => {
      console.error('[SETTINGS-QUERY] Error updating setting:', error);
      toast({
        title: 'Error',
        description: 'Failed to update setting',
        variant: 'destructive',
      });
    },
  });

  return {
    settings,
    isLoading,
    error,
    refetch,
    updateSetting: updateMutation.mutate,
    updateSettingAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};
